import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import type { GlossaryTermSummary } from '../services/glossaryService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

interface GlossaryContextValue {
  terms: GlossaryTermSummary[];
  termRegex: RegExp | null;
}

export const GlossaryContext = createContext<GlossaryContextValue>({
  terms: [],
  termRegex: null,
});

/** Build word-boundary case-insensitive regex from term list (longest first). */
export function buildTermRegex(terms: GlossaryTermSummary[]): RegExp | null {
  if (!terms.length) return null;
  const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
  const escaped = sorted.map(t => t.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');
}

export const GlossaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const { data: terms } = useQuery<GlossaryTermSummary[]>({
    queryKey: ['glossary-terms'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/knowledge/glossary`);
      if (!response.ok) return [];
      return response.json();
    },
    staleTime: Infinity,
    enabled: isAuthenticated,
  });

  const termRegex = useMemo(() => buildTermRegex(terms ?? []), [terms]);

  return (
    <GlossaryContext.Provider value={{ terms: terms ?? [], termRegex }}>
      {children}
    </GlossaryContext.Provider>
  );
};

export const useGlossary = () => useContext(GlossaryContext);
